import React from "react"
import { Tooltip } from "@mui/material"
import { LuChevronRight, LuInfo } from "react-icons/lu"
import { Arrow } from "../../../components/Icons/Icons"
import { Skeleton } from "../../../components/Skeleton/Skeleton"
import Sparkline from "./Sparkline"

/**
 * MetricCard — KPI card with a unified colored icon tile, elevation, a trend badge
 * (Arrow) and a mini sparkline. Colors use existing theme tokens only (semantic vars
 * + translucent tints); the base palette is unchanged.
 *
 * @param {string} title
 * @param {string|number} value
 * @param {string} subtitle
 * @param {number} percentChange
 * @param {number} absoluteChange - used instead of percent if provided
 * @param {boolean} invertColors - if true, increase is bad (red), decrease is good (green)
 * @param {string} comparisonLabel
 * @param {React.ElementType} icon - react-icons component for the tile (unified Lucide family)
 * @param {'indigo'|'success'|'error'} accent - tile + sparkline color theme
 * @param {number[]} spark - optional series for the mini sparkline
 */

const ACCENTS = {
  indigo:  { tileBg: 'rgba(94,106,210,0.14)', main: 'var(--color-primary-main)' },
  success: { tileBg: 'rgba(18,183,106,0.14)', main: 'var(--color-success-600)' },
  error:   { tileBg: 'rgba(240,68,56,0.14)',  main: 'var(--color-error-600)' },
}

const MetricCard = ({ title, value, subtitle, breakdown, percentChange, absoluteChange, invertColors = false, comparisonLabel, loading = false, icon: IconCmp, accent = 'indigo', spark, onClick, active = false, info, onDrilldown, drilldownLabel }) => {
  if (loading) {
    return (
      <div className="border border-divider rounded-2xl p-5 bg-surface shadow-card">
        <div className="flex items-center gap-3">
          <Skeleton width={44} height={44} />
          <Skeleton width={120} height={14} />
          {onDrilldown && <span className="ml-auto"><Skeleton width={28} height={28} /></span>}
        </div>
        <div className="pt-4"><Skeleton width={90} height={32} /></div>
        <div className="pt-3"><Skeleton width={140} height={22} /></div>
      </div>
    )
  }

  const cfg = ACCENTS[accent] || ACCENTS.indigo

  const changeValue = absoluteChange !== undefined ? absoluteChange : percentChange
  const showComparison = changeValue !== undefined && changeValue !== null && !isNaN(changeValue)
  const isPositive = changeValue > 0
  const isNeutral = changeValue === 0
  // Semantic direction: is this change GOOD? For most metrics up = good; for inverted
  // metrics (e.g. Canceled Client) a decrease is good.
  const isGood = invertColors ? changeValue < 0 : changeValue > 0

  // Colors follow MEANING (green = good, red = bad), applied to BOTH the trend badge and
  // the sparkline so they always agree. The arrow glyph still points the real direction.
  const dir = isNeutral
    ? { fill: 'var(--color-text-secondary)', fillBg: 'var(--color-gray-200)', text: 'text-text-secondary', spark: 'var(--color-text-secondary)' }
    : isGood
      ? { fill: 'var(--color-success-600)', fillBg: 'rgba(18,183,106,0.16)', text: 'text-success-600', spark: 'var(--color-success-600)' }
      : { fill: 'var(--color-error-600)', fillBg: 'rgba(240,68,56,0.16)', text: 'text-error-600', spark: 'var(--color-error-600)' }

  const formatChange = () => {
    const sign = isPositive ? '+' : ''
    if (absoluteChange !== undefined) return `${sign}${absoluteChange}`
    return `${sign}${percentChange.toFixed(1)}%`
  }

  const interactive = typeof onClick === 'function'

  return (
    <div
      className={`group flex flex-col rounded-2xl border bg-surface p-5 shadow-card transition-all duration-200 motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-lg ${interactive ? 'cursor-pointer' : ''} ${active ? 'border-primary-main' : 'border-divider'}`}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? active : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } } : undefined}
    >
      <div className="flex items-center gap-3">
        {IconCmp && (
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: cfg.tileBg }}
          >
            <IconCmp size={20} color={cfg.main} strokeWidth={2} />
          </span>
        )}
        <p className="font-semibold text-[14px] text-text-secondary">{title}</p>
        {info && (
          <Tooltip title={info} arrow placement="top" enterTouchDelay={0}>
            <span
              className="ml-auto flex h-5 w-5 items-center justify-center text-text-secondary hover:text-text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <LuInfo size={16} />
            </span>
          </Tooltip>
        )}
        {onDrilldown && (
          <Tooltip title={drilldownLabel || 'See the records behind this number'} arrow placement="top" enterTouchDelay={0}>
            <button
              type="button"
              aria-label={drilldownLabel || `See the records behind ${title}`}
              className={`${info ? '' : 'ml-auto'} flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-divider text-text-secondary hover:text-text-primary hover:bg-divider transition-colors`}
              onClick={(e) => { e.stopPropagation(); onDrilldown() }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
            >
              <LuChevronRight size={16} />
            </button>
          </Tooltip>
        )}
      </div>

      <p className="font-bold text-[32px] leading-tight pt-4 text-text-primary">{value}</p>

      <div className="flex items-end justify-between gap-3 pt-2">
        <div className="min-w-0">
          {showComparison && (
            <div className="flex items-center gap-1.5">
              <Arrow isArrowUp={isPositive} fill={dir.fill} fillBg={dir.fillBg} />
              <span className={`text-[14px] font-medium ${dir.text}`}>{formatChange()}</span>
              {comparisonLabel && (
                <span className="text-[13px] text-text-secondary ml-1 truncate">{comparisonLabel}</span>
              )}
            </div>
          )}
          {subtitle && <p className="text-[13px] text-text-secondary pt-1">{subtitle}</p>}
          {Array.isArray(breakdown) && breakdown.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-2">
              {breakdown.map((part) => (
                <span
                  key={part.key}
                  className="inline-flex items-center gap-1 rounded-full border border-divider px-2 py-0.5 text-[12px] text-text-secondary"
                >
                  <span className="text-text-primary font-medium">{part.label}</span>
                  <span className="tabular-nums">{part.value}</span>
                </span>
              ))}
            </div>
          )}
        </div>

        {Array.isArray(spark) && spark.length > 1 && (
          <div className="w-[42%] max-w-[160px] shrink-0">
            <Sparkline values={spark} color={dir.spark} height={44} />
          </div>
        )}
      </div>
    </div>
  )
}

export default MetricCard
